import {Box, Show, Text, VStack} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {EMPTY_PLACEHOLDER, suffixTestId} from 'shared/web';

import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {LevelFill} from '../LevelInput/LevelFill';
import {LevelInputProps} from '../LevelInput/LevelInputProps';
import {LevelInputRow} from '../LevelInput/LevelInputRow';
import {LevelPreview} from '../LevelInput/LevelPreview';
import {useLevelInput} from '../LevelInput/useLevelInput';

export type BatteryChargeProps = LevelInputProps;

/** Width the readout reserves so the row does not jog as the percentage changes. */
const READOUT_MIN_WIDTH = 7;

const blockVariants = cva(
  'flex w-[calc(var(--eag-space-step,0.25)*26rem)] flex-none items-center gap-px'
);
const bodyVariants = cva(
  'relative box-border flex h-[calc(var(--eag-space-step,0.25)*4rem)] min-w-0 flex-[1_1_auto] cursor-pointer select-none rounded-[var(--radius-small)] border border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-palettes-white-10-100)] [touch-action:none] focus-visible:[outline:var(--outline-default)_var(--color-palettes-blue-40-100)] focus-visible:[outline-offset:3px]',
  {
    variants: {
      isRecorded: {
        true: 'border-solid',
        false: 'border-dashed',
      },
    },
  }
);
const innerVariants = cva('absolute inset-[2px] overflow-hidden rounded-[var(--radius-x-small)]');
const terminalVariants = cva(
  'h-[8px] w-[3px] flex-none rounded-[var(--radius-x-small)] bg-[var(--color-palettes-neutral-50-100)]'
);

export function BatteryCharge(props: BatteryChargeProps) {
  const level = useLevelInput(props);

  return (
    <VStack>
      <Label
        isRequired={props.isRequired}
        tooltip={props.tooltip}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      <LevelInputRow
        icon="electricalParams/charging"
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('batteryCharge', props)}
      >
        <div className={cn(blockVariants())}>
          <div
            className={cn(bodyVariants({isRecorded: level.isRecorded}))}
            data-testid={suffixTestId('track', props)}
            {...level.trackProps}
          >
            <div className={cn(innerVariants())}>
              <Show when={level.isRecorded}>
                <LevelFill
                  $fraction={level.fraction ?? 0}
                  $bandColor={level.bandColor}
                  $radius="xSmall"
                />
              </Show>
              <Show when={level.hoverFraction}>
                <LevelPreview $fraction={level.hoverFraction ?? 0} />
              </Show>
            </div>
          </div>
          <div className={cn(terminalVariants())} />
        </div>
        <Box minWidth={READOUT_MIN_WIDTH} flexGrow={0} flexShrink={0}>
          <Text size="small" align="right" noWrap data-testid={suffixTestId('value', props)}>
            {level.valueText ?? EMPTY_PLACEHOLDER}
          </Text>
        </Box>
      </LevelInputRow>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
